const EMPTY_ADDRESS = { street: '', city: '', state: '', zipcode: '' };

const getStringValue = (value) => (value == null ? '' : String(value));
const getPhoneDigits = (value) => getStringValue(value).replace(/\D/g, '').slice(0, 10);

/** Format the account's ten-digit phone fields for editing. */
export const formatAccountPhoneNumber = (value) => {
  const digits = getPhoneDigits(value);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Read profile objects, including the JSON and character-map formats saved by older clients. */
const readProfileObject = (value) => {
  let parsed = value instanceof String ? value.toString() : value;

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const numericKeys = Object.keys(parsed).filter((key) => /^\d+$/.test(key));
    if (numericKeys.length) {
      parsed = numericKeys
        .sort((first, second) => Number(first) - Number(second))
        .map((key) => parsed[key])
        .join('');
    }
  }

  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }

  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
};

const getBirthdayForInput = (value) => {
  if (!value) return '';
  const date = getStringValue(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;

  const match = date.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (match) {
    const [, month, day, savedYear] = match;
    const year = savedYear.length === 2 ? `20${savedYear}` : savedYear;
    return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(0, 10);
};

const getBirthdayForApi = (value) => {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
};

/** Build the editable account fields without changing the server's profile format. */
export const getAccountProfile = (data = {}) => {
  const emergencyContact = readProfileObject(data.emergencyContact);

  return {
    name: getStringValue(data.name),
    email: getStringValue(data.email),
    employeeId: getStringValue(data.employeeId),
    phoneNumber: formatAccountPhoneNumber(data.phoneNumber),
    dob: getBirthdayForInput(data.dob),
    gender: getStringValue(data.gender) || 'Not Specified',
    address: { ...EMPTY_ADDRESS, ...readProfileObject(data.address) },
    emergencyContact: {
      name: getStringValue(emergencyContact.name),
      phone: formatAccountPhoneNumber(emergencyContact.phone),
    },
  };
};

/** Serialize the editable fields from a normalized account profile. */
export const getAccountProfileUpdate = (profile) => ({
  phoneNumber: getPhoneDigits(profile.phoneNumber),
  gender: profile.gender,
  birthday: getBirthdayForApi(profile.dob),
  address: JSON.stringify(profile.address),
  emergencyContact: JSON.stringify({
    ...profile.emergencyContact,
    phone: getPhoneDigits(profile.emergencyContact.phone),
  }),
});
